// ── Adapter Supabase do ICommissionRepository ─────────────────────
// Implementa a porta do módulo Comissões usando o cliente Supabase. É a
// ÚNICA camada que conhece as tabelas (commissions, config, times,
// time_members). Não persiste comissão direto — quem persiste é o trigger
// fn_create_commission_on_won (Config/26). Aqui só lê / agrega / grava taxas.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import { getPeriodRange, DEFAULT_RATES } from '@utils';
import type { CommissionRates } from '@/types/domain';
import type { Json } from '@/types/database.types';
import type {
  CommissionRawRow,
  NormalizedCommissionRow,
  UserProfileRef,
} from '@/types/commission';
import type {
  ICommissionRepository,
  ListCommissionsParams,
} from '@modules/commission';

const requireSupabase = (): TypedSupabaseClient => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase não inicializado.');
  return s;
};

// Puxa closer/sdr SEMPRE do card (fonte da verdade), não do snapshot da comissão.
const SELECT_RICH = `
  *,
  lead:leads ( id, nome, telefone, patrimonio ),
  card:cards (
    id, faturamento, won_at, is_turbo, sdr_id, closer_id,
    pipelines ( id, name, role ),
    closer:profiles!cards_closer_id_fkey ( id, nome, initials, color, avatar_url ),
    sdr:profiles!cards_sdr_id_fkey       ( id, nome, initials, color, avatar_url )
  )
`;

const normalizeCardOwners = (rows: CommissionRawRow[] | null | undefined): NormalizedCommissionRow[] =>
  (rows || []).map(c => {
    const cardCloserId = c.card?.closer_id;
    const cardSdrId    = c.card?.sdr_id;
    const newCloserAsSdr = !cardSdrId || cardSdrId === cardCloserId;

    // Se closer_as_sdr mudou em relação ao snapshot, recalcula sdr_commission.
    let sdrComm = Number(c.sdr_commission) || 0;
    if (newCloserAsSdr !== !!c.closer_as_sdr) {
      const fat     = Number(c.faturamento) || 0;
      const sdrRate = Number(c.sdr_rate)    || 0;
      sdrComm = newCloserAsSdr ? 0 : fat * sdrRate;
    }

    return {
      ...c,
      closer:         c.card?.closer || null,
      sdr:            c.card?.sdr    || null,
      closer_as_sdr:  newCloserAsSdr,
      sdr_commission: sdrComm,
    };
  });

export class SupabaseCommissionRepository implements ICommissionRepository {
  listCommissions = async ({
    workspaceId,
    period,
    closerId = null,
    sdrId    = null,
  }: ListCommissionsParams): Promise<NormalizedCommissionRow[]> => {
    if (!workspaceId || !period) return [];
    const supabase = requireSupabase();
    const range = getPeriodRange(period);
    if (!range) return [];

    const { data, error } = await supabase
      .from('commissions')
      .select(SELECT_RICH)
      .eq('workspace_id', workspaceId)
      .gte('won_date', range.start_date)
      .lte('won_date', range.end_date)
      .order('won_date', { ascending: false });
    if (error) throw error;

    // Filtra pelo responsável DO CARD (fonte da verdade), não do snapshot.
    let rows = normalizeCardOwners(data as unknown as CommissionRawRow[]);
    if (closerId) rows = rows.filter(c => c.card?.closer_id === closerId);
    if (sdrId)    rows = rows.filter(c => c.card?.sdr_id === sdrId);
    return rows;
  };

  getCommissionByCard = async (cardId: string | null | undefined): Promise<NormalizedCommissionRow | null> => {
    if (!cardId) return null;
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('commissions')
      .select(SELECT_RICH)
      .eq('card_id', cardId)
      .maybeSingle();
    if (error) throw error;
    return data ? normalizeCardOwners([data as unknown as CommissionRawRow])[0] : null;
  };

  getCommissionRates = async (workspaceId: string | null | undefined): Promise<CommissionRates> => {
    if (!workspaceId) return DEFAULT_RATES;
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('config')
      .select('value')
      .eq('workspace_id', workspaceId)
      .eq('key', 'commission_rates')
      .maybeSingle();
    if (error) throw error;
    return (data?.value as unknown as CommissionRates) || DEFAULT_RATES;
  };

  setCommissionRates = async (workspaceId: string, rates: CommissionRates): Promise<void> => {
    const supabase = requireSupabase();
    const { error } = await supabase
      .from('config')
      .upsert(
        {
          workspace_id: workspaceId,
          key: 'commission_rates',
          value: rates as unknown as Json,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'workspace_id,key' },
      );
    if (error) throw error;
  };

  listUsersByRole = async (workspaceId: string, role: 'sdr' | 'closer'): Promise<UserProfileRef[]> => {
    if (!workspaceId || !role) return [];
    const supabase = requireSupabase();

    const { data: times } = await supabase
      .from('times')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('ativo', true);
    const timeIds = (times || []).map(t => t.id);
    if (timeIds.length === 0) return [];

    const { data, error } = await supabase
      .from('time_members')
      .select(`user_id, role,
               profile:profiles!time_members_user_id_fkey ( id, nome, initials, color, avatar_url, ativo )`)
      .in('time_id', timeIds)
      .eq('role', role)
      .eq('ativo', true);
    if (error) throw error;

    // Deduplica por user_id e filtra profile ativo
    const seen = new Set<string>();
    const out: UserProfileRef[] = [];
    for (const r of ((data || []) as unknown as Array<{ profile: UserProfileRef | null }>)) {
      const p = r.profile;
      if (!p || p.ativo === false) continue;
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
    return out;
  };
}
